import { Hidden, Modal } from '@mui/material'
import IonIcon from '@reacticons/ionicons'
import cn from 'classnames'
import { omitBy } from 'lodash'
import { useRouter } from 'next/router'
import React, { memo, useEffect, useState } from 'react'
import { TailSpin } from 'react-loading-icons'
import { ContentList } from 'src/components/ContentList/ContentList'
import { FilterOptions } from 'src/components/FilterOptions/FilterOptions'
import { Filter } from 'src/components/global/filter'
import { SearchField } from 'src/components/global/SearchField'
import { Sidebar } from 'src/components/global/Sidebar'
import { SortbyDropdown, SortByType } from 'src/components/global/SortbyDropdown'
import { Pagination } from 'src/components/Pagination/Pagination'
import { SHOW_PRACTICE_AREAS } from 'src/config/config'
import {
  IContentItem,
  IEnum_Content_Contenttype,
  IEnum_Content_Documenttype,
  useSearchContentsLazyQuery,
} from 'src/graphql/generated/hooks'
// import { useAuthDefender } from 'src/hooks/useAuthDefender'
import { useUpdateQueryParams } from 'src/hooks/useUpdateQueryParams'
import { FilterOption } from 'src/types'

interface Props {
  filterProps: FilterOption[]
  showDocumentType?: boolean
  contentType?: IEnum_Content_Contenttype
  documentType?: IEnum_Content_Documenttype | string
  filtersDisabled?: boolean
  preAppliedFilters?: { [key: string]: string }
  sortType?: SortByType
  searchKeyword?: string
  onClickWhenDisabled?: () => void
  fromPage?: string
}

const pageSize = 10

export const ContentPagination: React.FC<Props> = memo(
  ({
    filterProps,
    showDocumentType = false,
    contentType,
    documentType,
    filtersDisabled = false,
    preAppliedFilters = {},
    sortType = 'contents',
    searchKeyword,
    onClickWhenDisabled,
    fromPage = '',
  }) => {
    const router = useRouter()
    const { updateQueryParams } = useUpdateQueryParams()
    const [currentPage, setCurrentPage] = useState(1)
    const [totalCount, setTotalCount] = useState(0)
    const [items, setCurrentItems] = useState<IContentItem[]>([])
    const [showFilterDlg, setShowFilterDlg] = useState(false)
    const [isInitialLoading, setIsInitialLoading] = useState(true)
    const [loadContents, { data: contentsData, loading }] = useSearchContentsLazyQuery()

    useEffect(() => {
      if (!router.isReady) return
      ;(async () => {
        await timeout(500)
        const curPage = router.query.page ? parseInt(router.query.page as string) : 1
        const indexOfLastPost = curPage * pageSize
        const indexOfFirstPost = indexOfLastPost - pageSize
        const filters = router.query

        let selection: string[] = []
        if (filters.productType) {
          const paths = (filters.productType as string).split('_')
          const checks = paths[0].split(',')

          checks.map((check) => {
            const subTopicDocumentId = check.split('@@@@')[1]
            selection.push(subTopicDocumentId)
          })
        }
        selection = [...new Set(selection)]
        const newProductType = selection.join(',')
        const query = filtersDisabled
          ? {
              ...preAppliedFilters,
              contentType,
              offset: 0,
              keyword: filters?.keyword,
              limit: pageSize,
              sort:
                filters?.sort || (sortType === 'category_contents' ? 'Category_Chronologically' : 'Chronologically'),
            }
          : {
              keyword: filters?.keyword,
              contentType: contentType || filters?.contentType,
              documentType: documentType || filters?.documentType,
              audienceIds: filters?.audiences,
              subjectIds: filters?.subject,
              phaseIds: filters?.phase,
              subjectMatterIds: filters?.subjectMatter,
              themeIds: filters?.theme,
              blogTaxonomyIds: filters?.blogTaxonomyIds,
              subTopicIds: filters ? newProductType : undefined,
              vendorTopicIds: filters?.vendorTopic,
              writerIds: filters?.author,
              practiceAreas: SHOW_PRACTICE_AREAS ? filters?.practiceAreas : undefined,
              startDate: filters?.startDate,
              endDate: filters?.endDate,
              offset: indexOfFirstPost,
              limit: pageSize,
              sort:
                filters?.sort || (sortType === 'category_contents' ? 'Category_Chronologically' : 'Chronologically'),
              ...preAppliedFilters,
            }
        loadContents({ variables: { query: omitBy(query, (value) => !value) } })
      })()
    }, [currentPage, filtersDisabled, router, router.isReady, router.query.keyword, router.query.sort])

    useEffect(() => {
      const curPage = router.query.page !== undefined ? parseInt(router.query.page as string) : 1
      if (contentsData) {
        const posts: IContentItem[] =
          contentsData.contentResults?.data?.contents?.filter((content): content is IContentItem => !!content) || []
        setCurrentItems(posts)
        setTotalCount(contentsData.contentResults?.data?.total || 0)
        setIsInitialLoading(false)
      }
      setCurrentPage(curPage)
    }, [contentsData])

    useEffect(() => {
      if (!loading && contentsData) {
        setIsInitialLoading(false)
      }
    }, [loading, contentsData])

    const navigateToPage = (page: number) => {
      updateQueryParams({ page: page.toString() }, true)
    }

    // Change page
    const paginateFront = () => {
      if (filtersDisabled) {
        onClickWhenDisabled && onClickWhenDisabled()
        return
      }
      const lastpage = totalCount / pageSize
      if (currentPage < lastpage) {
        setCurrentPage(currentPage + 1)
        navigateToPage(currentPage + 1)
      }
    }
    const paginateBack = () => {
      if (filtersDisabled) {
        onClickWhenDisabled && onClickWhenDisabled()
        return
      }
      if (currentPage > 1) {
        setCurrentPage(currentPage - 1)
        navigateToPage(currentPage - 1)
      }
    }
    const paginate = (pageNumber: number) => {
      if (filtersDisabled) {
        onClickWhenDisabled && onClickWhenDisabled()
        return
      }
      setCurrentPage(pageNumber)
      navigateToPage(pageNumber)
    }
    function timeout(delay: number) {
      return new Promise((res) => setTimeout(res, delay))
    }
    return (
      <div className="flex flex-col flex-1" key={fromPage + '-' + documentType + 'ContentPagination'}>
        <div
          className={cn(
            '',
            fromPage === 'SolutionVendorAnalysisContainer' ? 'min-[498px] pt-5 md:pt-10' : 'md:px-5 pt-5 md:pt-10',
          )}
        >
          <Hidden mdDown>
            <FilterOptions
              filterOptions={filterProps}
              className="md:my-[1rem] md:mt-0 mt-[16px] md:mb-0 mb-[20px] min-h-[28px] mr-[180px]"
            />
          </Hidden>

          <div
            className={cn(
              'flex flex-row justify-between md:items-center font-primary font-medium',
              fromPage === 'SolutionVendorAnalysisContainer' ? 'h-10 md:mb-[1rem] ' : 'h-10 md:mb-[1rem] gap-2',
            )}
          >
            <Hidden mdDown>
              {(loading || isInitialLoading) && (
                <div className="flex items-center">
                  <TailSpin stroke="#011D58" height="2em" /> <div className="pl-3"> Loading...</div>
                </div>
              )}
              {!loading && !isInitialLoading && (
                <div className="font-primary font-medium">
                  {(totalCount ?? 0) > 0
                    ? `${totalCount ?? 0} Content${(totalCount ?? 0) > 1 ? 's' : ''} Found`
                    : router.query.keyword || fromPage === 'ContentPaginationProductBriefing'
                      ? `No content found`
                      : ''}
                </div>
              )}
              <SortbyDropdown
                className="w-54 font-primary font-medium"
                searchKeyword={searchKeyword}
                type={sortType}
                fromPage={fromPage}
              />
            </Hidden>

            <Hidden mdUp>
              {fromPage !== 'SolutionVendorAnalysisContainer' && (
                <div className="flex flex-col">
                  <Filter onClickFilter={() => setShowFilterDlg(true)} />
                </div>
              )}
              {fromPage === 'SolutionVendorAnalysisContainer' ? (
                <SortbyDropdown
                  className="w-full font-primary font-medium"
                  searchKeyword={searchKeyword}
                  type={sortType}
                  fromPage={fromPage}
                />
              ) : (
                <SortbyDropdown
                  className="w-54 font-primary font-medium"
                  searchKeyword={searchKeyword}
                  type={sortType}
                  fromPage={fromPage}
                />
              )}
            </Hidden>

            {/* <Hidden mdUp>
              <div className="flex flex-col">
                <FilterOptions
                  filterOptions={filterProps}
                  className="md:my-[1rem] md:mt-0 mt-[16px] md:mb-0 mb-[20px] min-h-[28px]"
                />
              </div>
            </Hidden> */}
          </div>

          <Hidden mdUp>
            <div className="flex flex-col">
              {(loading || isInitialLoading) && (
                <div className="flex items-center">
                  <TailSpin stroke="#011D58" height="2em" /> <div className="pl-3"> Loading...</div>
                </div>
              )}
              {!loading && !isInitialLoading && (
                <div className="font-primary text-[12px] text-darkgray h-[20px] md:mb-[20px] mt-3 md:mt-[3px]">
                  {(totalCount ?? 0) > 0
                    ? `${totalCount ?? 0} Content${(totalCount ?? 0) > 1 ? 's' : ''} Found`
                    : router.query.keyword || fromPage === 'ContentPaginationProductBriefing'
                      ? `No content found`
                      : ''}
                </div>
              )}
              <FilterOptions
                filterOptions={filterProps}
                className="md:my-[1rem] md:mt-0 mt-[16px] md:mb-0 mb-[20px] min-h-[28px]"
              />
            </div>
          </Hidden>

          {/*  {(loading || isInitialLoading) && (
            <div className="w-full flex items-center justify-center my-[40px]">
              <TailSpin stroke="#011D58" height="2em" /> <div className="pl-3"> Loading...</div>
            </div>
          )} */}
          {!loading && !isInitialLoading && (
            <>
              <ContentList
                items={items}
                showDocumentType={showDocumentType}
                contentType={contentType}
                searchKeyword={searchKeyword}
              />
              {totalCount > 0 && (
                <Pagination
                  postsPerPage={pageSize}
                  totalPosts={totalCount}
                  paginateBack={paginateBack}
                  paginateFront={paginateFront}
                  paginate={paginate}
                  currentPage={currentPage}
                  className="md:pt-[1.875rem] pt-[0.875rem]"
                />
              )}
            </>
          )}
        </div>
        <Modal open={showFilterDlg}>
          <div className="flex flex-col w-full h-full bg-white p-[17px]">
            <div className="flex flex-row mb-[13px]">
              <p className="font-secondary font-bold text-[20px] text-darkgray items-center">Filter</p>
              <div className="ml-auto" onClick={() => setShowFilterDlg(false)}>
                <IonIcon name="close-outline" className="!h-[30px] !w-[30px] text-[black]" />
              </div>
            </div>
            <SearchField className="mb-[23px]" />
            <Sidebar filterOptions={filterProps} defaultSelectFilterIdx={1} />
            <button
              className="text-white font-secondary text-[14px] font-bold text-center rounded-[22.5px] px-[25px] bg-navy mt-auto mb-[15px] h-[50px] !important"
              onClick={() => setShowFilterDlg(false)}
            >
              Apply Filters
            </button>
          </div>
        </Modal>
      </div>
    )
  },
)
